import standard from '@config/standard';
import standardLegacy from '@config/standardLegacy';
import Service from '@services/Services';
import store from '@store/index'

/**
 * @typedef {Http}
 */
export default class Http extends Service {
  /**
   * @param {String} path
   * @param {Object} options
   * @param {Object} http
   */
  constructor(path, options = {}, http = null) {
    super(options);
    this.path = path;

    const standardConf = process.env.VUE_APP_NEW_AUTH_FLOW === 'true'
        ? standard
        : standardLegacy;

    this.http = http || standardConf;
  }

  /**
   * @param {String} path
   * @param {Object} options
   */
  static build(path, options) {
    return new this(path, options);
  }

  /**
   * @returns {Object}
   */
  getHttp() {
    if (store.state.auth.access_token) {
      this.http.defaults.headers.common['Authorization'] = store.state.auth.access_token;
    }

    if (process.env.VUE_APP_NEW_AUTH_FLOW === 'true') {
      const requestId = store.state.fingerprint && store.state.fingerprint.requestId
          ? store.state.fingerprint.requestId
          : null;

      if (requestId && requestId !== 'fingerprint_error') {
        this.http.defaults.headers.common['X-Fingerprint-RID'] = requestId;
      } else {
        delete this.http.defaults.headers.common['X-Fingerprint-RID'];
      }
    }

    let url = new URL(window.location)
    this.http.defaults.headers.common['X-Manual-Host'] = url.host;

    return this.http;
  }

  /**
   * @param {String} url
   * @returns {*|Promise<any>}
   */
  get(url) {
    return this.getHttp()
      .get(this.constructor.normalize(this.path, url))
      .then(this.constructor.then);
  }

  /**
   * @param {String} url
   * @param {Object} data
   * @param {Object} [config] - axios request config (e.g. `selfHandledError`)
   * @returns {*|Promise<any>}
   */
  post(url, data, config) {
    return this.getHttp()
      .post(this.constructor.normalize(this.path, url), data, config)
      .then(this.constructor.then);
  }

  /**
   * Makes a POST request returning Blob directly (without processing via Http.then)
   * Used for downloading binary files
   * 
   * @param {String} url
   * @param {Object} data
   * @returns {Promise<Blob>}
   */
  postBlob(url, data) {
    const http = this.getHttp();
    return http.post(this.constructor.normalize(this.path, url), data, {
      responseType: 'blob'
    }).then((response) => response.data);
  }

  /**
   * @param {String} url
   * @param {Object} data
   * @returns {*|Promise<any>}
   */
  put(url, data) {
    return this.getHttp()
      .put(this.constructor.normalize(this.path, url), data)
      .then(this.constructor.then);
  }

  /**
   * @param {String} url
   * @param {Object} data
   * @returns {*|Promise<any>}
   */
  patch(url, data) {
    return this.getHttp()
      .patch(this.constructor.normalize(this.path, url), data)
      .then(this.constructor.then);
  }

  /**
   * @param {String} url
   * @returns {*|Promise<any>}
   */
  delete(url) {
    return this.getHttp()
      .delete(this.constructor.normalize(this.path, url))
      .then(this.constructor.then);
  }

  /**
   * @param {Object} response
   * @returns {Object}
   */
  static then(response) {
    if (!response.data) {
      return {};
    }
    if (typeof response.data === 'string' &&
      (!response.data.includes('�') && !response.data.includes('%PDF'))) {
      return JSON.parse(response.data);
    }
    return response.data;
  }

  /**
   * @param {String} start
   * @param {String} end
   * @returns {String}
   */
  static normalize(start, end) {
    if (end.length > 0) {
      if (end[0] !== '?')
        return `${start}/${end}`.replace(/([^:]\/)\/+/g, '$1');
      else
        return `${start}${end}`.replace(/([^:]\/)\/+/g, '$1');
    } else {
      return `${start}`.replace(/([^:]\/)\/+/g, '$1');
    }
  }
}